import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    Divider,
    Empty,
    Form,
    Image,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Tooltip,
    Typography,
    message,
} from 'antd';
import {
    AimOutlined,
    ExclamationCircleOutlined,
    PictureOutlined,
    RadarChartOutlined,
    SwapOutlined,
    UploadOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const { Text, Title } = Typography;

// ─── Types ───────────────────────────────────────────────────────────────────

interface AreaOption {
    id: number;
    name: string;
    spaceType?: string;
}

interface SlamMap {
    id: number;
    name: string;
    description?: string | null;
    mapType: string;
    imageStoragePath?: string | null;
    imageUrl?: string | null;
    areaId?: number | null;
    area?: { id: number; name: string } | null;
    companyId: number;
    createdAt: string;
    updatedAt: string;
    metersPerPixel?: number | null;
    isDeleted: boolean;
}

// ─── Re-assign modal ─────────────────────────────────────────────────────────

const ReassignModal: React.FC<{
    open: boolean;
    map: SlamMap | null;
    areas: AreaOption[];
    onClose: () => void;
    onSaved: (updated: SlamMap) => void;
}> = ({ open, map, areas, onClose, onSaved }) => {
    const [selectedAreaId, setSelectedAreaId] = useState<number | null | undefined>(undefined);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open && map) {
            setSelectedAreaId(map.areaId ?? null);
        }
    }, [open, map]);

    const handleSave = async () => {
        if (!map) return;
        setSaving(true);
        try {
            const res = await axios.put(
                ENDPOINTS.SLAM_MAP_DETAIL(map.id),
                { areaId: selectedAreaId ?? null },
                { withCredentials: true }
            );
            const updated: SlamMap = res.data?.data || res.data;
            message.success('SLAM map re-assigned');
            onSaved(updated);
            onClose();
        } catch {
            message.error('Failed to re-assign SLAM map');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            title={
                <Space>
                    <SwapOutlined />
                    <span>Re-assign SLAM map to area</span>
                </Space>
            }
            open={open}
            onCancel={onClose}
            onOk={handleSave}
            okText="Save"
            okButtonProps={{ loading: saving }}
            destroyOnClose
        >
            {map && (
                <>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                        Map: <strong>{map.name}</strong>
                    </Text>
                    <Select
                        style={{ width: '100%' }}
                        allowClear
                        showSearch
                        placeholder="Select area / space (leave empty for company-wide)"
                        value={selectedAreaId ?? undefined}
                        onChange={(v) => setSelectedAreaId(v ?? null)}
                        filterOption={(input, option) =>
                            String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={areas.map(a => ({
                            value: a.id,
                            label: a.name,
                        }))}
                    />
                    <Text type="secondary" style={{ marginTop: 8, display: 'block', fontSize: 12 }}>
                        Choose which area or indoor space this SLAM map belongs to.
                        Leave empty to keep it available company-wide.
                    </Text>
                </>
            )}
        </Modal>
    );
};

// ─── Image preview cell ───────────────────────────────────────────────────────

const ThumbnailCell: React.FC<{ map: SlamMap }> = ({ map }) => {
    const [imageUrl, setImageUrl] = useState<string | null>(map.imageUrl || null);
    const [loading, setLoading] = useState(false);
    const [tried, setTried] = useState(!!map.imageUrl);

    useEffect(() => {
        if (!map.imageStoragePath || tried) return;
        setLoading(true);
        axios.get(ENDPOINTS.SLAM_MAP_IMAGE_URL(map.id), { withCredentials: true })
            .then(res => { setImageUrl(res.data?.data?.url || null); })
            .catch(() => { setImageUrl(null); })
            .finally(() => { setLoading(false); setTried(true); });
    }, [map.id, map.imageStoragePath, tried]);

    if (loading) return <Spin size="small" />;
    if (!imageUrl) return <PictureOutlined style={{ fontSize: 24, color: '#ccc' }} />;

    return (
        <Image
            src={imageUrl}
            width={56}
            height={40}
            style={{ objectFit: 'cover', borderRadius: 4, border: '1px solid #e8e8e8' }}
            placeholder={<Spin size="small" />}
            preview={{ mask: <span style={{ fontSize: 10 }}>Preview</span> }}
        />
    );
};

// ─── Upload modal ─────────────────────────────────────────────────────────────

const MAP_TYPE_OPTIONS = [
    { value: 'slam_2d', label: 'SLAM 2D (auto-generated scan)' },
    { value: 'floor_plan', label: 'Floor plan (uploaded image)' },
    { value: 'cad_overlay', label: 'CAD overlay' },
    { value: '3d_reconstruction', label: '3D reconstruction' },
];

/** Parse a ROS map.yaml text file client-side — mirrors the backend parser */
function parseRosYamlClientSide(text: string): { resolution?: number; originX?: number; originY?: number } {
    const result: { resolution?: number; originX?: number; originY?: number } = {};
    const resMatch = text.match(/^resolution:\s*([\d.eE+\-]+)/m);
    if (resMatch) {
        const v = parseFloat(resMatch[1]);
        if (Number.isFinite(v) && v > 0) result.resolution = v;
    }
    const originMatch = text.match(/^origin:\s*\[\s*([-\d.eE+]+)\s*,\s*([-\d.eE+]+)/m);
    if (originMatch) {
        const x = parseFloat(originMatch[1]);
        const y = parseFloat(originMatch[2]);
        if (Number.isFinite(x)) result.originX = x;
        if (Number.isFinite(y)) result.originY = y;
    }
    return result;
}

const UploadModal: React.FC<{
    open: boolean;
    areas: AreaOption[];
    onClose: () => void;
    onCreated: (map: SlamMap) => void;
}> = ({ open, areas, onClose, onCreated }) => {
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [yamlFile, setYamlFile] = useState<File | null>(null);
    const [yamlParsed, setYamlParsed] = useState<{ resolution?: number; originX?: number; originY?: number } | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const yamlInputRef = useRef<HTMLInputElement>(null);

    const handleClose = () => {
        form.resetFields();
        setImageFile(null);
        setYamlFile(null);
        setYamlParsed(null);
        onClose();
    };

    const handleYamlChange = async (file: File | null) => {
        setYamlFile(file);
        setYamlParsed(null);
        if (!file) return;
        try {
            const text = await file.text();
            const parsed = parseRosYamlClientSide(text);
            setYamlParsed(parsed);
            // Auto-fill form fields from YAML — only override if not already set by the user
            if (parsed.resolution !== undefined && !form.getFieldValue('metersPerPixel')) {
                form.setFieldsValue({ metersPerPixel: parsed.resolution });
            }
        } catch { /* ignore parse errors */ }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);

            // Step 1: Create the SLAM map record (JSON metadata)
            const createRes = await axios.post(ENDPOINTS.SLAM_MAPS, {
                name: values.name.trim(),
                description: values.description?.trim() || null,
                mapType: values.mapType || 'slam_2d',
                areaId: values.areaId || null,
                metersPerPixel: values.metersPerPixel || null,
            }, { withCredentials: true });

            const created: SlamMap = createRes.data?.data || createRes.data;

            // Step 2: Upload image (+ optional yaml) in a single multipart request
            if (imageFile && created.id) {
                try {
                    const formData = new FormData();
                    formData.append('image', imageFile);
                    if (yamlFile) formData.append('yaml', yamlFile);
                    console.log('[SlamMaps] Uploading image:', {
                        name: imageFile.name,
                        type: imageFile.type,
                        size: imageFile.size,
                        hasYaml: !!yamlFile,
                        yamlName: yamlFile?.name,
                        endpoint: ENDPOINTS.SLAM_MAP_IMAGE_UPLOAD(created.id),
                    });
                    const uploadRes = await axios.post(ENDPOINTS.SLAM_MAP_IMAGE_UPLOAD(created.id), formData, {
                        withCredentials: true,
                        // Don't set Content-Type header — browser sets multipart boundary automatically
                    });
                    console.log('[SlamMaps] Upload response:', JSON.stringify(uploadRes.data, null, 2));
                    // Merge the upload response into the created map so imageStoragePath is set
                    const uploadData = uploadRes.data?.data || uploadRes.data;
                    if (uploadData && typeof uploadData === 'object') {
                        Object.assign(created, uploadData);
                        // The back-end returns previewUrl — copy it to imageUrl so ThumbnailCell renders it
                        if (uploadData.previewUrl && !created.imageUrl) {
                            created.imageUrl = uploadData.previewUrl;
                        }
                    }
                } catch (uploadErr: any) {
                    console.error('[SlamMaps] Image upload FAILED:', {
                        status: uploadErr?.response?.status,
                        statusText: uploadErr?.response?.statusText,
                        data: uploadErr?.response?.data,
                        message: uploadErr?.message,
                    });
                    message.warning('Map created but image upload failed. You can upload the image later.');
                }
            }

            message.success(`SLAM map "${created.name}" created`);
            onCreated(created);
            handleClose();
        } catch (error: any) {
            if (error?.errorFields) return; // validation error
            const msg = error?.response?.data?.message || error?.message || 'Failed to create SLAM map';
            message.error(msg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            title={<Space><UploadOutlined /><span>Upload SLAM map</span></Space>}
            open={open}
            onCancel={handleClose}
            onOk={handleSubmit}
            okText="Upload"
            okButtonProps={{ loading: saving }}
            width={580}
            destroyOnClose
        >
            <Form form={form} layout="vertical" initialValues={{ mapType: 'slam_2d' }}>
                <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
                    <Input placeholder="e.g. Warehouse Level 2 — SLAM scan" />
                </Form.Item>

                <Form.Item name="description" label="Description">
                    <Input.TextArea rows={2} placeholder="Optional description" />
                </Form.Item>

                <Form.Item name="mapType" label="Map type" rules={[{ required: true }]}>
                    <Select options={MAP_TYPE_OPTIONS} />
                </Form.Item>

                <Form.Item name="areaId" label="Link to area">
                    <Select
                        allowClear
                        showSearch
                        placeholder="Leave blank for company-wide"
                        filterOption={(input, option) =>
                            String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={areas.filter(a => a.spaceType === 'indoor' || a.spaceType === 'mixed').map(a => ({
                            value: a.id,
                            label: `${a.spaceType === 'indoor' ? '🏠 ' : '⚡ '}${a.name}`,
                        }))}
                    />
                </Form.Item>

                <Form.Item
                    name="metersPerPixel"
                    label={
                        <Space size={4}>
                            Scale (metres per pixel)
                            {yamlParsed?.resolution !== undefined && (
                                <Text type="success" style={{ fontSize: 11 }}>
                                    ✓ auto-filled from YAML ({yamlParsed.resolution} m/px)
                                </Text>
                            )}
                        </Space>
                    }
                >
                    <InputNumber
                        style={{ width: '100%' }}
                        min={0.001}
                        step={0.01}
                        precision={4}
                        placeholder="e.g. 0.05"
                    />
                </Form.Item>

                <Divider plain>Map files</Divider>

                {/* Map image file */}
                <div style={{ marginBottom: 12 }}>
                    <Text strong style={{ display: 'block', marginBottom: 4 }}>
                        Map image <Text type="secondary">(PNG, JPEG, WebP or PGM)</Text>
                    </Text>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".png,.jpg,.jpeg,.webp,.pgm,.ppm,image/png,image/jpeg,image/webp"
                        style={{ display: 'none' }}
                        onChange={(e) => setImageFile(e.target.files?.[0] ?? null)}
                    />
                    <Space>
                        <Button
                            icon={<UploadOutlined />}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            {imageFile ? imageFile.name : 'Choose image file'}
                        </Button>
                        {imageFile && (
                            <Button
                                type="link"
                                size="small"
                                danger
                                onClick={() => {
                                    setImageFile(null);
                                    if (fileInputRef.current) fileInputRef.current.value = '';
                                }}
                            >
                                Remove
                            </Button>
                        )}
                    </Space>
                </div>

                {/* ROS YAML metadata file */}
                <div style={{ marginBottom: 8 }}>
                    <Text strong style={{ display: 'block', marginBottom: 4 }}>
                        ROS map.yaml <Text type="secondary">(optional — auto-fills resolution &amp; origin)</Text>
                    </Text>
                    <input
                        ref={yamlInputRef}
                        type="file"
                        accept=".yaml,.yml"
                        style={{ display: 'none' }}
                        onChange={(e) => handleYamlChange(e.target.files?.[0] ?? null)}
                    />
                    <Space>
                        <Button
                            onClick={() => yamlInputRef.current?.click()}
                        >
                            {yamlFile ? yamlFile.name : 'Choose .yaml file'}
                        </Button>
                        {yamlFile && (
                            <Button
                                type="link"
                                size="small"
                                danger
                                onClick={() => {
                                    setYamlFile(null);
                                    setYamlParsed(null);
                                    if (yamlInputRef.current) yamlInputRef.current.value = '';
                                }}
                            >
                                Remove
                            </Button>
                        )}
                    </Space>
                    {yamlParsed && (
                        <div style={{ marginTop: 6, padding: '4px 8px', background: '#f6ffed', borderRadius: 4, border: '1px solid #b7eb8f' }}>
                            <Text style={{ fontSize: 11 }}>
                                ✓ Parsed: resolution={yamlParsed.resolution ?? '—'} m/px
                                {yamlParsed.originX !== undefined && `, origin=[${yamlParsed.originX}, ${yamlParsed.originY}]`}
                            </Text>
                        </div>
                    )}
                </div>

                <Text type="secondary" style={{ fontSize: 11 }}>
                    Tip: upload both files from your ROS 2 map_saver output folder to auto-populate all metadata.
                </Text>
            </Form>
        </Modal>
    );
};

// ─── Re-upload image modal ────────────────────────────────────────────────────

const ReuploadImageModal: React.FC<{
    open: boolean;
    map: SlamMap | null;
    onClose: () => void;
    onUpdated: (updated: SlamMap) => void;
}> = ({ open, map, onClose, onUpdated }) => {
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [yamlFile, setYamlFile] = useState<File | null>(null);
    const [saving, setSaving] = useState(false);
    const imageInputRef = useRef<HTMLInputElement>(null);
    const yamlInputRef = useRef<HTMLInputElement>(null);

    const handleClose = () => {
        setImageFile(null);
        setYamlFile(null);
        onClose();
    };

    const handleUpload = async () => {
        if (!map || !imageFile) return;
        setSaving(true);
        try {
            const formData = new FormData();
            formData.append('image', imageFile);
            if (yamlFile) formData.append('yaml', yamlFile);
            console.log('[SlamMaps:ReuploadImageModal] Uploading:', {
                mapId: map.id,
                name: imageFile.name,
                type: imageFile.type,
                size: imageFile.size,
                hasYaml: !!yamlFile,
            });
            const res = await axios.post(ENDPOINTS.SLAM_MAP_IMAGE_UPLOAD(map.id), formData, { withCredentials: true });
            console.log('[SlamMaps:ReuploadImageModal] Response:', JSON.stringify(res.data, null, 2));
            message.success('Image uploaded successfully');
            const updated: SlamMap = res.data?.data || res.data;
            onUpdated({ ...map, ...updated });
            handleClose();
        } catch (err: any) {
            console.error('[SlamMaps:ReuploadImageModal] FAILED:', {
                status: err?.response?.status,
                data: err?.response?.data,
                message: err?.message,
            });
            message.error(err?.response?.data?.message || 'Image upload failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            title={<Space><UploadOutlined /><span>Upload map image{map ? ` — ${map.name}` : ''}</span></Space>}
            open={open}
            onCancel={handleClose}
            onOk={handleUpload}
            okText="Upload"
            okButtonProps={{ loading: saving, disabled: !imageFile }}
            destroyOnClose
        >
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
                <div>
                    <Text strong style={{ display: 'block', marginBottom: 4 }}>
                        Map image <Text type="secondary">(PNG, JPEG, WebP or PGM)</Text>
                    </Text>
                    <input
                        ref={imageInputRef}
                        type="file"
                        accept=".png,.jpg,.jpeg,.webp,.pgm,.ppm,image/png,image/jpeg,image/webp"
                        style={{ display: 'none' }}
                        onChange={(e) => setImageFile(e.target.files?.[0] ?? null)}
                    />
                    <Space>
                        <Button icon={<UploadOutlined />} onClick={() => imageInputRef.current?.click()}>
                            {imageFile ? imageFile.name : 'Choose image file'}
                        </Button>
                        {imageFile && (
                            <Button type="link" size="small" danger
                                onClick={() => { setImageFile(null); if (imageInputRef.current) imageInputRef.current.value = ''; }}>
                                Remove
                            </Button>
                        )}
                    </Space>
                </div>
                <div>
                    <Text strong style={{ display: 'block', marginBottom: 4 }}>
                        ROS map.yaml <Text type="secondary">(optional)</Text>
                    </Text>
                    <input
                        ref={yamlInputRef}
                        type="file"
                        accept=".yaml,.yml"
                        style={{ display: 'none' }}
                        onChange={(e) => setYamlFile(e.target.files?.[0] ?? null)}
                    />
                    <Space>
                        <Button onClick={() => yamlInputRef.current?.click()}>
                            {yamlFile ? yamlFile.name : 'Choose .yaml file'}
                        </Button>
                        {yamlFile && (
                            <Button type="link" size="small" danger
                                onClick={() => { setYamlFile(null); if (yamlInputRef.current) yamlInputRef.current.value = ''; }}>
                                Remove
                            </Button>
                        )}
                    </Space>
                </div>
            </Space>
        </Modal>
    );
};

// ─── Main view ───────────────────────────────────────────────────────────────

const SlamMaps: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const currentCompanyId = currentUser?.currentCompanyId || currentUser?.companyId;

    const [maps, setMaps] = useState<SlamMap[]>([]);
    const [areas, setAreas] = useState<AreaOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [reassignMap, setReassignMap] = useState<SlamMap | null>(null);
    const [reuploadMap, setReuploadMap] = useState<SlamMap | null>(null);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [rootAreaId, setRootAreaId] = useState<number | null>(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [mapsRes, areasRes, companyRes] = await Promise.all([
                axios.get(ENDPOINTS.SLAM_MAPS, { withCredentials: true }),
                axios.get(ENDPOINTS.AREAS_V2, { withCredentials: true }),
                currentCompanyId
                    ? axios.get(`${ENDPOINTS.GET_COMPANIES}/${currentCompanyId}`, { withCredentials: true })
                    : Promise.resolve({ data: null }),
            ]);

            const rawMaps: SlamMap[] = mapsRes.data?.data || mapsRes.data || [];
            const rawAreas: AreaOption[] = areasRes.data?.data || areasRes.data || [];
            const companyData = companyRes.data;

            setMaps(rawMaps.filter(m => !m.isDeleted));
            setAreas(rawAreas);
            if (companyData?.rootAreaId) {
                setRootAreaId(companyData.rootAreaId);
            }
        } catch {
            message.error('Failed to load SLAM maps');
        } finally {
            setLoading(false);
        }
    }, [currentCompanyId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const areaMap = useMemo(() => {
        const m: Record<number, AreaOption> = {};
        areas.forEach(a => { m[a.id] = a; });
        return m;
    }, [areas]);

    const handleDelete = (map: SlamMap) => {
        Modal.confirm({
            title: 'Delete SLAM Map',
            icon: <ExclamationCircleOutlined />,
            content: `Delete "${map.name}"? This cannot be undone.`,
            okText: 'Delete',
            okType: 'danger',
            onOk: async () => {
                try {
                    await axios.delete(ENDPOINTS.SLAM_MAP_DETAIL(map.id), { withCredentials: true });
                    message.success(`"${map.name}" deleted`);
                    setMaps(prev => prev.filter(m => m.id !== map.id));
                } catch {
                    message.error('Failed to delete SLAM map');
                }
            }
        });
    };

    const handleCreateScanMission = (areaId?: number | null) => {
        // Navigate to Operations with pre-filled query params for a SLAM scan mission
        const targetAreaId = areaId ?? rootAreaId;
        if (targetAreaId) {
            navigate(`/operations?newMission=1&missionType=slam_scan&areaId=${targetAreaId}`);
        } else {
            navigate('/operations?newMission=1&missionType=slam_scan');
        }
    };

    const handleReassignSaved = (updated: SlamMap) => {
        setMaps(prev => prev.map(m => m.id === updated.id ? { ...m, areaId: updated.areaId } : m));
    };

    const mapTypeColor: Record<string, string> = {
        slam_2d: 'blue',
        floor_plan: 'green',
        cad_overlay: 'orange',
        '3d_reconstruction': 'purple',
    };

    const columns = [
        {
            title: '',
            key: 'thumb',
            width: 72,
            render: (_: any, record: SlamMap) => <ThumbnailCell map={record} />,
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, record: SlamMap) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{text}</Text>
                    {record.description && <Text type="secondary" style={{ fontSize: 12 }}>{record.description}</Text>}
                </Space>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'mapType',
            key: 'mapType',
            render: (t: string) => <Tag color={mapTypeColor[t] || 'default'}>{t.replace(/_/g, ' ')}</Tag>,
        },
        {
            title: 'Linked area',
            dataIndex: 'areaId',
            key: 'area',
            render: (areaId: number | null) => {
                if (!areaId) return <Text type="secondary">Company-wide</Text>;
                const a = areaMap[areaId];
                if (!a) return <Text type="secondary">#{areaId}</Text>;
                const color = a.spaceType === 'indoor' ? 'purple' : a.spaceType === 'mixed' ? 'orange' : 'blue';
                return <Tag color={color}>{a.name}</Tag>;
            },
        },
        {
            title: 'Scale',
            dataIndex: 'metersPerPixel',
            key: 'scale',
            render: (v: number | null) => v ? <Text>{v} m/px</Text> : <Text type="secondary">—</Text>,
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (d: string) => new Date(d).toLocaleDateString(),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: SlamMap) => (
                <Space wrap>
                    {!record.imageStoragePath && (
                        <Tooltip title="Upload map image (PGM/PNG)">
                            <Button
                                size="small"
                                type="dashed"
                                icon={<UploadOutlined />}
                                onClick={() => setReuploadMap(record)}
                            >
                                Upload image
                            </Button>
                        </Tooltip>
                    )}
                    <Tooltip title="Re-assign to area">
                        <Button
                            size="small"
                            icon={<SwapOutlined />}
                            onClick={() => setReassignMap(record)}
                        >
                            Assign
                        </Button>
                    </Tooltip>
                    <Tooltip title="Create scan mission from this area">
                        <Button
                            size="small"
                            type="primary"
                            ghost
                            icon={<RadarChartOutlined />}
                            onClick={() => handleCreateScanMission(record.areaId)}
                        >
                            New scan
                        </Button>
                    </Tooltip>
                    <Button
                        size="small"
                        danger
                        onClick={() => handleDelete(record)}
                    >
                        Delete
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div className="areas-page">
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                <Col>
                    <Space direction="vertical" size={0}>
                        <Title level={2} style={{ margin: 0 }}>SLAM Maps</Title>
                        <Text type="secondary">
                            All scanned maps for your company. Re-assign to spaces or create new scan missions.
                        </Text>
                    </Space>
                </Col>
                <Col>
                    <Space>
                        {!rootAreaId && (
                            <Alert
                                type="warning"
                                showIcon
                                message="No root area configured"
                                description="New scan missions without a specific area will not be pre-linked. Set a root area in Company Settings."
                                style={{ maxWidth: 320 }}
                            />
                        )}
                        <Button
                            type="primary"
                            icon={<RadarChartOutlined />}
                            onClick={() => handleCreateScanMission(null)}
                        >
                            New scan mission
                        </Button>
                        <Button
                            icon={<UploadOutlined />}
                            onClick={() => setUploadOpen(true)}
                        >
                            Upload map
                        </Button>
                    </Space>
                </Col>
            </Row>

            {rootAreaId && (
                <Alert
                    type="info"
                    showIcon
                    icon={<AimOutlined />}
                    message={
                        <span>
                            Root area: <strong>{areaMap[rootAreaId]?.name ?? `#${rootAreaId}`}</strong> —
                            scan missions without a specific area will be linked here.
                        </span>
                    }
                    style={{ marginBottom: 16 }}
                />
            )}

            <Card>
                <Table
                    loading={loading}
                    dataSource={maps}
                    columns={columns}
                    rowKey="id"
                    size="small"
                    pagination={{ pageSize: 20, showSizeChanger: true }}
                    locale={{ emptyText: <Empty description="No SLAM maps yet. Create a scan mission to generate one, or upload manually." /> }}
                />
            </Card>

            <ReassignModal
                open={!!reassignMap}
                map={reassignMap}
                areas={areas}
                onClose={() => setReassignMap(null)}
                onSaved={handleReassignSaved}
            />

            <UploadModal
                open={uploadOpen}
                areas={areas}
                onClose={() => setUploadOpen(false)}
                onCreated={(newMap) => setMaps(prev => [newMap, ...prev])}
            />

            <ReuploadImageModal
                open={!!reuploadMap}
                map={reuploadMap}
                onClose={() => setReuploadMap(null)}
                onUpdated={(updated) => {
                    setMaps(prev => prev.map(m => m.id === updated.id ? { ...m, ...updated } : m));
                    setReuploadMap(null);
                }}
            />
        </div>
    );
};

export default SlamMaps;
